/**
 * MessageInboxBell — dedicated customer-reply inbox (iter174).
 *
 * Separate from the general activity NotificationBell. This icon lights up
 * ONLY when a customer replies to one of our SMS (notification_type =
 * 'sms_reply'). SM wanted a clean "message inbox" alert instead of digging
 * customer replies out of the noisy job-card/activity bell.
 */
import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessageInboxBell() {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/notifications?limit=10&notification_type=sms_reply`,
        { credentials: 'include' }
      );
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      /* silent — next poll retries */
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    const onFocus = () => fetchMessages();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchMessages]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notification_ids: [notificationId] }),
      });
      setMessages((prev) =>
        prev.map((n) => (n.notification_id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      /* silent */
    }
  };

  const markAllRead = async () => {
    // Mark just the sms_reply notifications we know about as read.
    const ids = messages.filter((n) => !n.is_read).map((n) => n.notification_id);
    if (ids.length === 0) return;
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notification_ids: ids }),
      });
      setMessages((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      /* silent */
    }
  };

  const handleClick = (n) => {
    if (!n.is_read) markAsRead(n.notification_id);
    if (n.link) {
      navigate(n.link);
      setIsOpen(false);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="message-inbox-bell"
          title="Customer message replies"
        >
          <MessageSquare className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-emerald-500 text-white text-xs animate-pulse"
              data-testid="message-inbox-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 max-h-96 overflow-y-auto"
        data-testid="message-inbox-dropdown"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4 text-emerald-600" />
            Customer Replies
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={markAllRead}
              data-testid="message-inbox-mark-all-read"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {messages.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No customer replies yet</p>
          </div>
        ) : (
          <div className="py-1">
            {messages.map((n) => (
              <div
                key={n.notification_id}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-50 flex gap-3 ${
                  !n.is_read ? 'bg-emerald-50/60' : ''
                }`}
                onClick={() => handleClick(n)}
                data-testid={`message-inbox-item-${n.notification_id}`}
              >
                <div className="mt-0.5 p-1.5 rounded-full bg-emerald-100 text-emerald-600">
                  <MessageSquare className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${!n.is_read ? 'font-semibold' : ''}`}>
                    {n.title}
                  </p>
                  <p className="text-xs text-slate-500 line-clamp-2">{n.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(n.created_at)}</p>
                </div>
                {!n.is_read && <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />}
              </div>
            ))}
          </div>
        )}

        {messages.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                onClick={() => {
                  navigate('/comms');
                  setIsOpen(false);
                }}
                data-testid="message-inbox-open-comms"
              >
                Open Comms
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
